/**
 * LoginPage.jsx — CAI Login Screen
 *
 * Features:
 *  - Email + password form with validation
 *  - Loading state with spinner during authentication
 *  - Error display for failed login attempts
 *  - Demo credentials hint for ABC presentation
 *  - Forgot Password — multi-step inline flow:
 *      Step 1: Enter email
 *      Step 2: Enter 6-digit OTP (or "contact admin" if mode=admin)
 *      Step 3: Set new password
 *      Step 4: Success screen
 *  - Fully accessible (keyboard navigation, ARIA labels)
 *  - Mobile responsive
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, ArrowLeft, CheckCircle2, Mail, KeyRound, ShieldCheck, Check } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../context/AuthContext';
import { requestPasswordReset, verifyResetOTP, resetPassword } from '../utils/api';
import logger from '../utils/logger';
import './LoginPage.css';

// ── Forgot Password — multi-step flow ─────────────────────────────────────────
// resetStep: 0 = login form, 1 = enter email, 2 = enter OTP, 3 = new password, 4 = success

function ForgotPasswordFlow({ onBack }) {
  const [resetStep,    setResetStep]    = useState(1);
  const [resetEmail,   setResetEmail]   = useState('');
  const [otp,          setOtp]          = useState('');
  const [newPassword,  setNewPassword]  = useState('');
  const [confirmPwd,   setConfirmPwd]   = useState('');
  const [showNewPwd,   setShowNewPwd]   = useState(false);
  const [resetToken,   setResetToken]   = useState('');
  const [adminMode,    setAdminMode]    = useState(false);  // true = show "contact admin"
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [successMsg,   setSuccessMsg]   = useState('');

  // ── Step 1: Submit email ──────────────────────────────────────────────────
  const handleRequestOTP = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (!resetEmail.trim() || !resetEmail.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await requestPasswordReset(resetEmail.trim().toLowerCase());
      if (res.mode === 'admin') {
        setAdminMode(true);
        setSuccessMsg(res.message);
        setResetStep(2); // show admin message
      } else {
        setResetStep(2); // show OTP entry
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resetEmail]);

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOTP = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      const res = await verifyResetOTP(resetEmail.trim().toLowerCase(), otp);
      setResetToken(res.reset_token);
      setResetStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resetEmail, otp]);

  // ── Step 3: Set new password ──────────────────────────────────────────────
  const handleResetPassword = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPwd) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(resetToken, newPassword);
      setResetStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resetToken, newPassword, confirmPwd]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="login-card">

      {/* ── Step 1: Enter email ── */}
      {resetStep === 1 && (
        <>
          <div className="login-card-header">
            <div className="reset-step-icon"><Mail size={22} /></div>
            <h1 className="login-title">Reset your password</h1>
            <p className="login-subtitle">
              Enter your registered email. We'll send a 6-digit code.
            </p>
          </div>
          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}
          <form className="login-form" onSubmit={handleRequestOTP} noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="reset-email">Email address</label>
              <input
                id="reset-email"
                type="email"
                className="login-input"
                value={resetEmail}
                onChange={e => { setResetEmail(e.target.value); setError(''); }}
                placeholder="you@company.com"
                autoFocus
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <button type="submit" className="login-btn-primary" disabled={loading}>
              {loading
                ? <><Loader2 size={18} className="login-spinner" /> Sending code…</>
                : 'Send reset code'}
            </button>
          </form>
          <button className="reset-back-link" onClick={onBack} disabled={loading}>
            <ArrowLeft size={14} /> Back to sign in
          </button>
        </>
      )}

      {/* ── Step 2a: Admin-only mode message ── */}
      {resetStep === 2 && adminMode && (
        <>
          <div className="login-card-header">
            <div className="reset-step-icon reset-step-icon--warning"><ShieldCheck size={22} /></div>
            <h1 className="login-title">Contact your admin</h1>
            <p className="login-subtitle">{successMsg}</p>
          </div>
          <button className="login-btn-primary" onClick={onBack} style={{ marginTop: 8 }}>
            Back to sign in
          </button>
        </>
      )}

      {/* ── Step 2b: OTP entry ── */}
      {resetStep === 2 && !adminMode && (
        <>
          <div className="login-card-header">
            <div className="reset-step-icon"><KeyRound size={22} /></div>
            <h1 className="login-title">Enter your code</h1>
            <p className="login-subtitle">
              A 6-digit code was sent to <strong className="reset-email-highlight">{resetEmail}</strong>.
              It expires in 10 minutes.
            </p>
          </div>
          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}
          <form className="login-form" onSubmit={handleVerifyOTP} noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="otp-input">6-digit code</label>
              <input
                id="otp-input"
                type="text"
                inputMode="numeric"
                className="login-input reset-otp-input"
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="000000"
                maxLength={6}
                autoFocus
                disabled={loading}
                autoComplete="one-time-code"
              />
            </div>
            <button type="submit" className="login-btn-primary" disabled={loading || otp.length !== 6}>
              {loading
                ? <><Loader2 size={18} className="login-spinner" /> Verifying…</>
                : 'Verify code'}
            </button>
          </form>
          <div className="reset-resend-row">
            <span className="reset-resend-label">Didn't receive it?</span>
            <button
              className="reset-resend-btn"
              disabled={loading}
              onClick={() => { setResetStep(1); setOtp(''); setError(''); }}
            >
              Resend code
            </button>
          </div>
          <button className="reset-back-link" onClick={onBack} disabled={loading}>
            <ArrowLeft size={14} /> Back to sign in
          </button>
        </>
      )}

      {/* ── Step 3: New password ── */}
      {resetStep === 3 && (
        <>
          <div className="login-card-header">
            <div className="reset-step-icon reset-step-icon--success"><ShieldCheck size={22} /></div>
            <h1 className="login-title">Set new password</h1>
            <p className="login-subtitle">
              Choose a new password for <strong className="reset-email-highlight">{resetEmail}</strong>.
            </p>
          </div>
          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}
          <form className="login-form" onSubmit={handleResetPassword} noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="new-pwd">New password</label>
              <div className="login-password-wrapper">
                <input
                  id="new-pwd"
                  type={showNewPwd ? 'text' : 'password'}
                  className="login-input login-input--password"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  placeholder="Min 6 characters"
                  autoFocus
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowNewPwd(v => !v)}
                  disabled={loading}
                  aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                >
                  {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="login-field">
              <label className="login-label" htmlFor="confirm-pwd">Confirm password</label>
              <input
                id="confirm-pwd"
                type="password"
                className="login-input"
                value={confirmPwd}
                onChange={e => { setConfirmPwd(e.target.value); setError(''); }}
                placeholder="Repeat your new password"
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="login-btn-primary" disabled={loading}>
              {loading
                ? <><Loader2 size={18} className="login-spinner" /> Saving…</>
                : 'Set new password'}
            </button>
          </form>
          <button className="reset-back-link" onClick={onBack} disabled={loading}>
            <ArrowLeft size={14} /> Back to sign in
          </button>
        </>
      )}

      {/* ── Step 4: Success ── */}
      {resetStep === 4 && (
        <>
          <div className="login-card-header" style={{ textAlign: 'center' }}>
            <div className="reset-step-icon reset-step-icon--success reset-step-icon--lg">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="login-title">Password updated</h1>
            <p className="login-subtitle">
              Your password has been changed successfully. Sign in with your new password.
            </p>
          </div>
          <button className="login-btn-primary" onClick={onBack}>
            Sign in
          </button>
        </>
      )}
    </div>
  );
}


// ─── Main Login Component ─────────────────────────────────────────────────────

export default function LoginPage() {
  const { login, loading, error, clearError } = useAuth();

  // Form state
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors,  setFieldErrors]  = useState({});

  // Forgot password
  const [showReset, setShowReset] = useState(false);

  // ── Client-side validation ────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errors = {};
    if (!email.trim())             errors.email    = 'Email is required.';
    else if (!email.includes('@')) errors.email    = 'Enter a valid email address.';
    if (!password)                 errors.password = 'Password is required.';
    return errors;
  }, [email, password]);

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    clearError();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    logger.info('Login: Submitting form', { email });
    await login(email, password);
  }, [email, password, login, validate, clearError]);


  // ── Back from reset flow ──────────────────────────────────────────────────
  const handleBackFromReset = useCallback(() => {
    setShowReset(false);
    clearError();
  }, [clearError]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-bg-grid" aria-hidden="true" />
      <div className="login-orb login-orb--1" aria-hidden="true" />
      <div className="login-orb login-orb--2" aria-hidden="true" />

      <div className="login-container">

        {/* ── Brand ── */}
        <div className="login-brand">
          <div className="login-logo-badge">
            <BrandLogo size={44} />
          </div>
          <div className="login-brand-text">
            <span className="login-brand-name">Querely<sup className="tm">™</sup></span>
            <span className="login-brand-tagline">Your SAP, in plain English</span>
          </div>
        </div>

        {/* ── Forgot Password Flow ── */}
        {showReset ? (
          <ForgotPasswordFlow onBack={handleBackFromReset} />
        ) : (

          /* ── Login Card ── */
          <div className="login-card">
            <div className="login-card-header">
              <h1 className="login-title">Welcome back</h1>
              <p className="login-subtitle">Your SAP copilot for transactions, tables, OData services and business processes.</p>
            </div>

            {error && (
              <div className="login-error" role="alert" aria-live="polite">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="login-field">
                <label htmlFor="email" className="login-label">Email address</label>
                <input
                  id="email"
                  type="email"
                  className={`login-input ${fieldErrors.email ? 'login-input--error' : ''}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  aria-invalid={!!fieldErrors.email}
                />
                {fieldErrors.email && (
                  <span id="email-error" className="login-field-error" role="alert">
                    {fieldErrors.email}
                  </span>
                )}
              </div>

              <div className="login-field">
                <div className="login-label-row">
                  <label htmlFor="password" className="login-label">Password</label>
                  <button
                    type="button"
                    className="login-forgot-link"
                    onClick={() => { clearError(); setShowReset(true); }}
                    disabled={loading}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="login-password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`login-input login-input--password ${fieldErrors.password ? 'login-input--error' : ''}`}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearError(); }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={loading}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                    aria-invalid={!!fieldErrors.password}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <span id="password-error" className="login-field-error" role="alert">
                    {fieldErrors.password}
                  </span>
                )}
              </div>

              <button type="submit" className="login-btn-primary" disabled={loading} aria-busy={loading}>
                {loading
                  ? <><Loader2 size={18} className="login-spinner" /><span>Signing in…</span></>
                  : <span>Sign in</span>}
              </button>
            </form>

            <div className="login-trust">
              <span className="login-trust-item"><Check size={13} />Enterprise Secure</span>
              <span className="login-trust-item"><Check size={13} />SAP S/4HANA Ready</span>
              <span className="login-trust-item"><Check size={13} />Role-Based Access</span>
            </div>

          </div>
        )}

        {/* ── Footer ── */}
        <p className="login-footer">
          Querely v1.0 &nbsp;·&nbsp; Powered by SAP OData + Azure OpenAI
          <br />
          <span className="login-footer-company">© 2026 Inqlect Technologies Pvt Ltd — Confidential</span>
          <br />
          <span className="login-footer-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <span aria-hidden="true"> &nbsp;·&nbsp; </span>
            <Link to="/terms">Terms of Service</Link>
          </span>
        </p>
      </div>
    </div>
  );
}
